import { Fragment, useMemo, useState } from 'react';

import {
  useAffinityListConfigs,
  useApproveRsvp,
  useBulkApproveRsvp,
  useCampaignRsvpApprovals,
  useDismissRsvp,
} from '../../hooks/useOutreach';
import type {
  Campaign,
  EventStatusMap,
  RsvpApprovalRow,
  RsvpApprovalStatus,
} from '../../types/outreach';
import { formatEtShort } from '../../utils/formatTimestamp';
import styles from '../pages/OutreachPage.module.css';
import { EnrollmentKindBadge } from './CampaignRecipientsTable';
import { RefreshButton } from './RefreshButton';

/**
 * A row needs a per-row operator pick before approving when:
 *   - the classifier returned 'maybe' (couldn't decide), or
 *   - it's a reply trigger with a null verdict (classifier-unavailable
 *     fallback, Path I).
 * Both cases land with no action pre-filled, so we force the radio
 * picker before Approve enables. Bulk-approve also skips these rows.
 */
function needsOperatorPick(r: RsvpApprovalRow): boolean {
  if (r.classifier_verdict === 'maybe') return true;
  if (r.classifier_verdict === null && r.trigger_source === 'reply') return true;
  return false;
}


/**
 * Per-campaign queue of pending RSVP actions awaiting operator approval.
 *
 * Each row represents "the engine thinks we should do X for this
 * recipient" -- where X is some combination of:
 *   * proposed_status_event: write this list status event
 *   * proposed_send_invite: send a Microsoft Graph calendar event
 *
 * For 'maybe' verdicts (Sonnet couldn't decide), the row has neither
 * action pre-filled; the operator picks Yes/No/Just-invite via inline
 * radio buttons before Approve becomes enabled.
 *
 * The 'has_prior_sent_invite' badge surfaces the Path H case: this
 * recipient has a prior sent invite on this campaign, so the operator
 * should think carefully before approving a follow-up action (e.g. a
 * change-of-mind reply landing after the initial yes).
 */
export function RsvpApprovalQueue({
  campaignId,
  campaign,
}: {
  campaignId: number;
  campaign: Campaign;
}) {
  const [statusPill, setStatusPill] = useState<RsvpApprovalStatus | ''>('pending');
  const queueQ = useCampaignRsvpApprovals(campaignId, statusPill);
  // Already-cached query (the Campaigns + Forms tabs use it too) — read
  // this campaign's list config so the row expander can name the status
  // labels the campaign actually writes instead of hardcoded AGM ones.
  const listsQ = useAffinityListConfigs();
  const eventStatusMap = listsQ.data?.find(
    (l) => l.affinity_list_id === campaign.affinity_list_id,
  )?.event_to_status_map;
  const approveMut = useApproveRsvp();
  const dismissMut = useDismissRsvp();
  const bulkApproveMut = useBulkApproveRsvp();

  // Track per-row operator overrides for `maybe` verdicts AND for
  // classifier-unavailable rows (reply + null verdict). Both flows need
  // a Yes/No/Just-invite pick before Approve enables.
  const [maybeOverrides, setMaybeOverrides] = useState<
    Record<number, 'yes' | 'no' | 'just_invite' | undefined>
  >({});

  // Multi-select state for bulk approve. Keyed by approval id.
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  // Row expander: which row's detail panel is open. Single-expanded so
  // the table stays scannable.
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const data = queueQ.data;

  // Sort the visible rows: errors first (need attention), then pending,
  // then by created_at desc within each status.
  const sortedRows = useMemo(() => {
    if (!data) return [];
    const statusRank: Record<string, number> = {
      send_error: 0,
      pending: 1,
      sent: 2,
      dismissed: 3,
    };
    return [...data.rows].sort((a, b) => {
      const sr = (statusRank[a.status] ?? 9) - (statusRank[b.status] ?? 9);
      if (sr !== 0) return sr;
      return b.created_at.localeCompare(a.created_at);
    });
  }, [data]);

  const eventConfigured =
    campaign.event_starts_at != null && campaign.event_ends_at != null;

  // Rows eligible for bulk approval: pending, action pre-filled (not
  // needing operator pick), with event configured if invite is required.
  const eligibleIds = useMemo(
    () => sortedRows
      .filter((r) => {
        if (r.status !== 'pending') return false;
        if (needsOperatorPick(r)) return false;
        if (r.proposed_send_invite && !eventConfigured) return false;
        return true;
      })
      .map((r) => r.id),
    [sortedRows, eventConfigured],
  );

  if (queueQ.isLoading) return <div className={styles.empty}>Loading…</div>;
  if (queueQ.isError) {
    return (
      <div className={styles.empty} style={{ color: '#B04030' }}>
        Failed to load queue:{' '}
        {queueQ.error instanceof Error ? queueQ.error.message : 'unknown'}
      </div>
    );
  }
  if (!data) return <div className={styles.empty}>No queue data.</div>;

  function recipientName(r: RsvpApprovalRow): string {
    const n = [r.recipient_first_name, r.recipient_last_name]
      .filter(Boolean).join(' ');
    return n || r.recipient_email;
  }

  /**
   * Human-friendly trigger label for the queue row. The raw enum
   * (`form` / `reply` / `affinity_poll` / `manual`) is a poor mental
   * model for the operator — they want to know what caused the row
   * AND what direction it's heading (yes / no / invite). Inferred from
   * the trigger + classifier verdict + proposed action set:
   *
   *   affinity_poll → "List → RSVP Yes"     (poll only fires on yes-flips)
   *   reply         → "Replied: <verdict>"  ("classifier unavailable" when null)
   *   form          → "Form: Yes"           (form-no never enqueues)
   *   manual        → "Manual: Yes / No / Invite" (derived from action set)
   */
  function triggerLabel(r: RsvpApprovalRow): string {
    switch (r.trigger_source) {
      case 'affinity_poll':
        return 'List → RSVP Yes';
      case 'reply': {
        if (r.classifier_verdict == null) return 'Replied: classifier unavailable';
        const verdict = r.classifier_verdict.charAt(0).toUpperCase()
          + r.classifier_verdict.slice(1);
        return `Replied: ${verdict}`;
      }
      case 'form':
        return 'Form: Yes';
      case 'manual': {
        if (r.proposed_status_event === 'rsvp_attending') return 'Manual: Yes';
        if (r.proposed_status_event === 'rsvp_not_attending') return 'Manual: No';
        if (r.proposed_send_invite) return 'Manual: Just-invite';
        return 'Manual';
      }
      default:
        return r.trigger_source;
    }
  }

  function confidenceChip(
    r: RsvpApprovalRow,
  ): { text: string; bg: string; fg: string } | null {
    // Only meaningful on reply rows where the classifier actually ran.
    if (r.trigger_source !== 'reply' || r.classifier_confidence == null) {
      return null;
    }
    switch (r.classifier_confidence) {
      case 'high':   return { text: 'high conf',   bg: '#E5F4E5', fg: '#2E6E2E' };
      case 'medium': return { text: 'med conf',    bg: '#FFF7E0', fg: '#7B5400' };
      case 'low':    return { text: 'low conf',    bg: '#FBEBD8', fg: '#7B3500' };
      default:       return null;
    }
  }

  function actionChip(r: RsvpApprovalRow): { text: string; color: string } {
    const wantsStatus = r.proposed_status_event != null;
    const wantsInvite = r.proposed_send_invite;
    if (r.classifier_verdict === 'maybe' && !wantsStatus && !wantsInvite) {
      return { text: 'Operator picks', color: '#7B5400' };
    }
    if (wantsStatus && wantsInvite) {
      return { text: 'Status + Invite', color: '#2E6E2E' };
    }
    if (wantsStatus) {
      return { text: 'Status only', color: '#2A4A8A' };
    }
    if (wantsInvite) {
      return { text: 'Invite only', color: '#2A4A8A' };
    }
    return { text: 'No-op', color: '#999' };
  }

  function verdictChip(r: RsvpApprovalRow): { text: string; bg: string; fg: string } | null {
    switch (r.classifier_verdict) {
      case 'yes':       return { text: 'yes',       bg: '#E5F4E5', fg: '#2E6E2E' };
      case 'no':        return { text: 'no',        bg: '#FBEBD8', fg: '#7B3500' };
      case 'maybe':     return { text: 'maybe',     bg: '#FFF7E0', fg: '#7B5400' };
      case 'unrelated': return { text: 'unrelated', bg: '#F0F0EC', fg: '#999' };
      default:          return null;
    }
  }

  async function handleApprove(r: RsvpApprovalRow) {
    let override_status_event: string | null = null;
    let override_send_invite: boolean | null = null;
    if (needsOperatorPick(r)) {
      const pick = maybeOverrides[r.id];
      if (!pick) {
        alert('Pick Yes / No / Just-invite before approving this row.');
        return;
      }
      if (pick === 'yes') {
        override_status_event = 'rsvp_attending';
        override_send_invite = true;
      } else if (pick === 'no') {
        override_status_event = 'rsvp_not_attending';
        override_send_invite = false;
      } else {  // just_invite
        override_status_event = null;
        override_send_invite = true;
      }
    }
    if (
      (override_send_invite ?? r.proposed_send_invite)
      && !eventConfigured
    ) {
      alert(
        'This campaign has no event date/time configured. Set Event '
        + 'Details on the Overview tab before approving an invite.',
      );
      return;
    }
    try {
      const res = await approveMut.mutateAsync({
        approvalId: r.id,
        campaignId,
        override_status_event,
        override_send_invite,
      });
      if (res.status === 'send_error') {
        alert(
          `Send error: ${res.error_step ?? '?'}: ${res.error_text ?? '?'}. `
          + 'The status write may have landed; check the row + retry.',
        );
      }
    } catch (e: unknown) {
      alert(`Approve failed: ${e instanceof Error ? e.message : 'unknown'}`);
    }
  }

  async function handleDismiss(r: RsvpApprovalRow) {
    if (!window.confirm(
      `Dismiss the RSVP queue row for ${recipientName(r)}? No actions `
      + 'will execute. The recipient\'s current list status stays.',
    )) return;
    try {
      await dismissMut.mutateAsync({ approvalId: r.id, campaignId });
    } catch (e: unknown) {
      alert(`Dismiss failed: ${e instanceof Error ? e.message : 'unknown'}`);
    }
  }

  function toggleSelected(id: number, checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(id); else next.delete(id);
      return next;
    });
  }

  // Selectable rows = pending, not-needing-operator-pick, with the
  // event configured if invite is required. Selecting an ineligible row
  // would just produce an error result from the backend; cleaner to
  // disable the checkbox.
  function canBulkSelect(r: RsvpApprovalRow): boolean {
    if (r.status !== 'pending') return false;
    if (needsOperatorPick(r)) return false;
    if (r.proposed_send_invite && !eventConfigured) return false;
    return true;
  }

  async function handleBulkApprove() {
    const ids = sortedRows
      .filter((r) => selectedIds.has(r.id) && canBulkSelect(r))
      .map((r) => r.id);
    if (ids.length === 0) {
      alert('No eligible rows selected.');
      return;
    }
    if (!window.confirm(
      `Bulk-approve ${ids.length} row${ids.length === 1 ? '' : 's'}? `
      + 'This will write list statuses and send calendar invites where '
      + 'configured. Per-row failures will land as send_error.',
    )) return;
    try {
      const res = await bulkApproveMut.mutateAsync({
        approvalIds: ids,
        campaignId,
      });
      setSelectedIds(new Set());
      if (res.failed > 0) {
        const lines = res.results
          .filter((it) => it.status !== 'sent')
          .map((it) => `#${it.id}: ${it.status} -- ${it.error_text ?? '?'}`)
          .slice(0, 8);
        alert(
          `Bulk approve done: ${res.succeeded} sent, ${res.failed} failed.\n\n`
          + lines.join('\n')
          + (res.failed > 8 ? `\n... (+${res.failed - 8} more)` : ''),
        );
      }
    } catch (e: unknown) {
      alert(`Bulk approve failed: ${e instanceof Error ? e.message : 'unknown'}`);
    }
  }

  const pills: { value: RsvpApprovalStatus | ''; label: string; count: number }[] = [
    { value: 'pending',    label: 'Pending',    count: data.by_status.pending    ?? 0 },
    { value: 'sent',       label: 'Sent',       count: data.by_status.sent       ?? 0 },
    { value: 'send_error', label: 'Errors',     count: data.by_status.send_error ?? 0 },
    { value: 'dismissed',  label: 'Dismissed',  count: data.by_status.dismissed  ?? 0 },
    { value: '',           label: 'All',        count: data.total                     },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {!eventConfigured && (
        <div
          style={{
            background: '#FFF7E0',
            border: '1px solid #F0D070',
            color: '#7B5400',
            padding: '10px 12px',
            borderRadius: 6,
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          ⚠ No event date/time set for this campaign. Approvals that
          include sending a calendar invite will refuse until you
          configure <strong>Event Details</strong> on the Overview tab.
        </div>
      )}
      {eventConfigured && !campaign.calendar_invites_enabled && (
        <div
          style={{
            background: '#FFF7E0',
            border: '1px solid #F0D070',
            color: '#7B5400',
            padding: '10px 12px',
            borderRadius: 6,
            fontSize: 13,
            marginBottom: 12,
          }}
        >
          ⚠ Calendar invites are <strong>paused</strong> for this campaign.
          Approve will refuse to send calendar invites until you turn{' '}
          <strong>Send calendar invites</strong> on (Overview tab → Event
          Details). The queue is still populating normally; Approve
          without invite (override) still works.
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
        {pills.map((p) => (
          <button
            key={p.value || 'all'}
            type="button"
            className={`${styles.button} ${statusPill === p.value ? styles.buttonPrimary : ''}`}
            onClick={() => setStatusPill(p.value)}
          >
            {p.label} ({p.count})
          </button>
        ))}
      </div>

      <div
        style={{
          display: 'flex', gap: 8, alignItems: 'center', marginBottom: 12,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 12, color: '#666' }}>
          Showing {sortedRows.length} of {data.total}
        </span>
        <RefreshButton
          onClick={() => queueQ.refetch()}
          isFetching={queueQ.isFetching}
          title="Re-fetch the RSVP queue"
        />
        {statusPill === 'pending' && eligibleIds.length > 0 && (
          <>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 12, color: '#666' }}>
              {selectedIds.size} selected
            </span>
            <button
              type="button"
              className={styles.button}
              onClick={() => setSelectedIds(new Set(eligibleIds))}
              disabled={selectedIds.size === eligibleIds.length}
              title="Select all rows where the action is unambiguous"
            >
              Select all eligible ({eligibleIds.length})
            </button>
            <button
              type="button"
              className={styles.button}
              onClick={() => setSelectedIds(new Set())}
              disabled={selectedIds.size === 0}
            >
              Clear
            </button>
            <button
              type="button"
              className={`${styles.button} ${styles.buttonPrimary}`}
              onClick={handleBulkApprove}
              disabled={selectedIds.size === 0 || bulkApproveMut.isPending}
              title="Approve every selected row in one request"
            >
              {bulkApproveMut.isPending
                ? `Approving ${selectedIds.size}…`
                : `Bulk Approve (${selectedIds.size})`}
            </button>
          </>
        )}
      </div>

      {sortedRows.length === 0 ? (
        <div className={styles.empty}>
          {statusPill
            ? `No rows in status "${statusPill}".`
            : 'No queue rows yet. RSVPs land here for operator approval.'}
        </div>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              {statusPill === 'pending' && eligibleIds.length > 0 && (
                <th style={{ width: 1 }}>
                  <input
                    type="checkbox"
                    checked={
                      eligibleIds.length > 0
                      && eligibleIds.every((id) => selectedIds.has(id))
                    }
                    onChange={(e) => {
                      setSelectedIds(
                        e.target.checked ? new Set(eligibleIds) : new Set(),
                      );
                    }}
                    title="Select / clear all eligible rows"
                  />
                </th>
              )}
              <th>Recipient</th>
              <th>Trigger</th>
              <th>Verdict</th>
              <th>Action</th>
              <th>Excerpt</th>
              <th>Created</th>
              <th>Status</th>
              <th style={{ width: 1 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((r) => {
              const action = actionChip(r);
              const verdict = verdictChip(r);
              const needsPick = needsOperatorPick(r) && r.status === 'pending';
              const classifierUnavailable =
                r.classifier_verdict === null
                && r.trigger_source === 'reply'
                && r.status === 'pending';
              const pick = maybeOverrides[r.id];
              const canApprove =
                r.status === 'pending' || r.status === 'send_error';
              const canDismiss = r.status === 'pending';
              const selectable = canBulkSelect(r);
              const cols =
                statusPill === 'pending' && eligibleIds.length > 0 ? 9 : 8;
              return (
                <Fragment key={r.id}>
                <tr>
                  {statusPill === 'pending' && eligibleIds.length > 0 && (
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(r.id)}
                        disabled={!selectable}
                        onChange={(e) =>
                          toggleSelected(r.id, e.target.checked)
                        }
                        title={
                          selectable
                            ? 'Include in bulk approve'
                            : needsPick
                              ? 'Needs per-row operator pick'
                              : r.proposed_send_invite && !eventConfigured
                                ? 'Event metadata missing'
                                : ''
                        }
                      />
                    </td>
                  )}
                  <td>
                    <div style={{ fontSize: 13 }}>
                      {recipientName(r)}
                      <EnrollmentKindBadge kind={r.enrollment_kind} />
                      {r.has_prior_sent_invite && (
                        <span
                          style={{
                            marginLeft: 6, fontSize: 10, padding: '1px 6px',
                            background: '#FBEBD8', color: '#7B3500',
                            borderRadius: 8,
                          }}
                          title="A prior calendar invite was already sent to this recipient on this campaign. Review carefully."
                        >
                          prior sent invite
                        </span>
                      )}
                    </div>
                    <div className={styles.helpText}>
                      {r.recipient_email}
                      {r.recipient_company && ` · ${r.recipient_company}`}
                    </div>
                  </td>
                  <td style={{ fontSize: 12 }}>
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedId((id) => (id === r.id ? null : r.id))
                      }
                      style={{
                        background: 'none', border: 0, padding: 0,
                        cursor: 'pointer', font: 'inherit', textAlign: 'left',
                        color: '#1a4f8a',
                      }}
                      title="Click to expand row details"
                    >
                      {triggerLabel(r)} {expandedId === r.id ? '▴' : '▾'}
                    </button>
                    {(() => {
                      const c = confidenceChip(r);
                      if (!c) return null;
                      return (
                        <span
                          style={{
                            display: 'inline-block', marginLeft: 6,
                            padding: '1px 6px', borderRadius: 8,
                            fontSize: 10, background: c.bg, color: c.fg,
                            fontFamily: "'DM Mono', monospace",
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                          }}
                          title="Sonnet's self-rated confidence in the verdict"
                        >
                          {c.text}
                        </span>
                      );
                    })()}
                  </td>
                  <td>
                    {verdict ? (
                      <span
                        style={{
                          display: 'inline-block', padding: '2px 8px',
                          borderRadius: 8, fontSize: 11,
                          background: verdict.bg, color: verdict.fg,
                          fontFamily: "'DM Mono', monospace",
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                        }}
                      >
                        {verdict.text}
                      </span>
                    ) : classifierUnavailable ? (
                      <span
                        style={{
                          display: 'inline-block', padding: '2px 8px',
                          borderRadius: 8, fontSize: 11,
                          background: '#FBEBD8', color: '#7B3500',
                          fontFamily: "'DM Mono', monospace",
                          textTransform: 'uppercase',
                          letterSpacing: '0.04em',
                        }}
                        title="The reply classifier was unavailable (budget exhausted or retries failed). Read the excerpt and pick an action."
                      >
                        classifier unavailable
                      </span>
                    ) : (
                      <span className={styles.helpText}>—</span>
                    )}
                  </td>
                  <td>
                    <div style={{ color: action.color, fontSize: 12 }}>
                      {action.text}
                    </div>
                    {needsPick && (
                      <div style={{ marginTop: 4, fontSize: 11 }}>
                        {(['yes', 'no', 'just_invite'] as const).map((opt) => (
                          <label
                            key={opt}
                            style={{ marginRight: 8, cursor: 'pointer' }}
                          >
                            <input
                              type="radio"
                              name={`pick-${r.id}`}
                              checked={pick === opt}
                              onChange={() =>
                                setMaybeOverrides((m) => ({ ...m, [r.id]: opt }))
                              }
                              style={{ marginRight: 2 }}
                            />
                            {opt === 'just_invite' ? 'invite' : opt}
                          </label>
                        ))}
                      </div>
                    )}
                  </td>
                  <td style={{ maxWidth: 280, fontSize: 12 }}>
                    {r.classifier_excerpt ? (
                      <span title={r.classifier_excerpt}>
                        {r.classifier_excerpt.length > 100
                          ? r.classifier_excerpt.slice(0, 100) + '…'
                          : r.classifier_excerpt}
                      </span>
                    ) : (
                      <span className={styles.helpText}>—</span>
                    )}
                  </td>
                  <td className={styles.code} style={{ fontSize: 11 }}>
                    {formatEtShort(r.created_at)}
                  </td>
                  <td>
                    <StatusBadge status={r.status} />
                    {r.error_text && (
                      <div
                        className={styles.helpText}
                        style={{ color: '#B04030', fontSize: 11 }}
                        title={r.error_text}
                      >
                        {r.error_text.slice(0, 60)}
                        {r.error_text.length > 60 ? '…' : ''}
                      </div>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6, whiteSpace: 'nowrap' }}>
                      {canApprove && (
                        <button
                          type="button"
                          className={`${styles.button} ${styles.buttonPrimary}`}
                          onClick={() => handleApprove(r)}
                          disabled={
                            approveMut.isPending
                            || (needsPick && !pick)
                          }
                          title={
                            needsPick && !pick
                              ? 'Pick Yes/No/Invite first'
                              : r.status === 'send_error'
                                ? 'Retry: re-attempt the failed step'
                                : 'Approve and execute the action set'
                          }
                        >
                          {r.status === 'send_error'
                            ? (approveMut.isPending ? 'Retrying…' : 'Retry')
                            : (approveMut.isPending ? 'Approving…' : 'Approve')}
                        </button>
                      )}
                      {canDismiss && (
                        <button
                          type="button"
                          className={`${styles.button} ${styles.buttonDanger}`}
                          onClick={() => handleDismiss(r)}
                          disabled={dismissMut.isPending}
                          title="Dismiss the row without firing any actions"
                        >
                          Dismiss
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                {expandedId === r.id && (
                  <ExpanderRow
                    r={r}
                    colSpan={cols}
                    eventStatusMap={eventStatusMap}
                  />
                )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}


/**
 * Detail panel that drops below a queue row when the operator clicks
 * the trigger label. Shows the full classifier excerpt + reasoning, the
 * list status this row would write, sent timestamps, and any
 * error_text. The table cell spans the full width so the panel reads
 * like a sub-section rather than another row.
 */
function ExpanderRow({
  r, colSpan, eventStatusMap,
}: {
  r: RsvpApprovalRow;
  colSpan: number;
  /** The list config's event → status-label map, when it has loaded. */
  eventStatusMap?: EventStatusMap;
}) {
  function label(text: string) {
    return (
      <div
        style={{
          fontSize: 10, color: '#888', textTransform: 'uppercase',
          letterSpacing: '0.04em', marginBottom: 2,
        }}
      >
        {text}
      </div>
    );
  }

  // The status label this row writes on approve, read from the list
  // config's own event map rather than hardcoded AGM labels. Falls back
  // to neutral wording when the config hasn't loaded / maps the event to
  // nothing.
  let targetStatus: string;
  if (!r.proposed_status_event) {
    targetStatus = '— (no status change)';
  } else if (eventStatusMap?.[r.proposed_status_event]) {
    targetStatus = eventStatusMap[r.proposed_status_event] as string;
  } else if (eventStatusMap) {
    // Map loaded and this event maps to nothing: approve writes no status.
    targetStatus = `— (${r.proposed_status_event} is not mapped to a status)`;
  } else {
    targetStatus = 'status on approve (per the list config)';
  }

  const isReply = r.trigger_source === 'reply';

  return (
    <tr style={{ background: '#FAF9F4' }}>
      <td colSpan={colSpan} style={{ padding: '12px 16px' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '12px 24px',
            fontSize: 12,
          }}
        >
          {isReply && r.classifier_excerpt && (
            <div style={{ gridColumn: '1 / -1' }}>
              {label('Reply excerpt')}
              <div
                style={{
                  background: '#FFFFFF',
                  border: '1px solid #E5E5E0',
                  borderRadius: 4,
                  padding: '8px 10px',
                  fontSize: 12,
                  whiteSpace: 'pre-wrap',
                }}
              >
                {r.classifier_excerpt}
              </div>
            </div>
          )}
          {isReply && r.classifier_reasoning
            && r.classifier_reasoning !== 'classifier unavailable; operator picks' && (
            <div style={{ gridColumn: '1 / -1' }}>
              {label('Classifier reasoning')}
              <div style={{ fontSize: 12 }}>{r.classifier_reasoning}</div>
            </div>
          )}
          {r.classifier_reasoning === 'classifier unavailable; operator picks' && (
            <div style={{ gridColumn: '1 / -1', color: '#7B3500' }}>
              {label('Classifier reasoning')}
              <div style={{ fontSize: 12 }}>
                Sonnet was unavailable for this reply (budget exhausted
                or retries failed). Pick Yes / No / Just-invite manually
                from the action column.
              </div>
            </div>
          )}
          <div>
            {label('List status on approve')}
            <div>
              <span style={{ color: '#666' }}>
                current: <code style={{ fontFamily: "'DM Mono', monospace" }}>
                  {/* Not surfaced on the row schema; surface the recipient's
                      current label if we had it. For now omit and just show
                      the target, which is the actionable part. */}
                </code>
              </span>
              <span style={{ fontWeight: 500 }}>{targetStatus}</span>
            </div>
          </div>
          <div>
            {label('Calendar invite')}
            <div>{r.proposed_send_invite ? 'Will send on approve' : 'No invite'}</div>
          </div>
          {r.sent_at && (
            <div>
              {label('Sent at')}
              <div>{r.sent_at}</div>
            </div>
          )}
          {r.approved_by && (
            <div>
              {label('Approved by')}
              <div>{r.approved_by}</div>
            </div>
          )}
          {r.graph_event_id && (
            <div style={{ gridColumn: '1 / -1' }}>
              {label('Graph event id')}
              <code
                style={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: 11, wordBreak: 'break-all',
                }}
              >
                {r.graph_event_id}
              </code>
            </div>
          )}
          {r.error_text && (
            <div style={{ gridColumn: '1 / -1', color: '#B04030' }}>
              {label('Last error')}
              <div style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>
                {r.error_text}
              </div>
            </div>
          )}
        </div>
      </td>
    </tr>
  );
}


function StatusBadge({ status }: { status: RsvpApprovalStatus }) {
  const colorMap: Record<RsvpApprovalStatus, { bg: string; fg: string }> = {
    pending:    { bg: '#F0F0EC', fg: '#666' },
    sent:       { bg: '#E5F4E5', fg: '#2E6E2E' },
    send_error: { bg: '#FBEBD8', fg: '#7B3500' },
    dismissed:  { bg: '#F0F0EC', fg: '#999' },
  };
  const { bg, fg } = colorMap[status];
  return (
    <span
      style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: 10,
        background: bg, color: fg, fontSize: 11,
        fontFamily: "'DM Mono', monospace",
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      {status}
    </span>
  );
}
